import { CaptionedImage, DetailsPanel } from '@/components/controls';
import { useNichePersonImage } from '@/hooks';
import { toProperCase } from '@/utils';
import { Map as MapIcon, Repeat as RepeatIcon } from '@mui/icons-material';
import {
  Box,
  Card,
  Chip,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { format } from 'date-fns';

function Victim({ victim }) {
  const {
    query: { data: imageSrc, isFetching },
  } = useNichePersonImage(victim?.id);

  return (
    <Stack spacing={0.5} sx={{ minWidth: 200 }}>
      {isFetching ? (
        <Skeleton variant="rounded" sx={{ minWidth: 200 }} height={240} />
      ) : (
        <CaptionedImage
          imageSrc={imageSrc}
          height={240}
          fallbackWidth={200}
          topCaption={
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <Box>Aggrieved</Box>
              {victim?.repeat && (
                <Tooltip title="Repeat victim" placement="top-end" arrow>
                  <RepeatIcon color="secondary" sx={{ fontSize: 16 }} />
                </Tooltip>
              )}
            </Stack>
          }
          bottomCaption={toProperCase(
            [victim?.forenames, victim?.surname].filter(Boolean).join(' '),
          )}
        />
      )}
      {victim?.vulnerabilities?.length > 0 && (
        <Stack direction="row" spacing={0.5} sx={{ overflow: 'scroll' }}>
          {victim.vulnerabilities.map((vulnerability) => (
            <Chip key={vulnerability} label={vulnerability} size="small" />
          ))}
        </Stack>
      )}
    </Stack>
  );
}

export function SexualOffence({ offence, onShowMapClick }) {
  const { occurrence, aggrieved = [], subject } = offence;
  const isRepeatVictim = aggrieved.some((victim) => victim.repeat);
  const {
    query: { data: subjectImageSrc, isFetching: subjectImageFetching },
  } = useNichePersonImage(subject?.id);

  return (
    <Card
      variant="outlined"
      sx={(theme) => ({
        minWidth: 240,
        bgcolor: isRepeatVictim
          ? alpha(theme.palette.secondary.main, 0.2)
          : undefined,
        borderColor: isRepeatVictim ? theme.palette.secondary.dark : undefined,
      })}
    >
      <Stack
        direction="row"
        spacing={1}
        sx={{ overflowX: 'auto', px: 1, pt: 1 }}
      >
        {subject?.id ? (
          subjectImageFetching ? (
            <Skeleton variant="rounded" sx={{ minWidth: 200 }} height={240} />
          ) : (
            <CaptionedImage
              imageSrc={subjectImageSrc}
              height={240}
              fallbackWidth={200}
              topCaption="Subject"
              bottomCaption={toProperCase(
                [subject?.forenames, subject?.surname]
                  .filter(Boolean)
                  .join(' '),
              )}
            />
          )
        ) : null}
        {aggrieved.map((victim) => (
          <Victim key={victim.id} victim={victim} />
        ))}
      </Stack>
      <Stack spacing={1} sx={{ p: 1 }}>
        <Stack
          direction="row"
          spacing={0.5}
          sx={{ alignItems: 'center', justifyContent: 'space-between' }}
        >
          <Typography
            variant="body3"
            color="textSecondary"
            sx={{ flexGrow: 1 }}
          >
            {occurrence?.number}
          </Typography>
          <Typography variant="subtitle2">
            {occurrence?.reportedTime &&
              format(occurrence.reportedTime, 'dd/MM/yyyy HH:mm')}
          </Typography>
        </Stack>
        {occurrence?.summary && (
          <Typography variant="body2" sx={{ p: 0.5 }}>
            {occurrence.summary}
          </Typography>
        )}
        <DetailsPanel
          title="Owned by"
          entries={[
            {
              label: 'OIC',
              value: occurrence?.officerInCase?.collarNumber,
            },
            {
              label: 'Team',
              value: occurrence?.officerInCase?.team,
            },
          ]}
        />
      </Stack>
      <Stack
        direction="row"
        spacing={1}
        sx={{ px: 1, pb: 1, justifyContent: 'flex-end', alignItems: 'center' }}
      >
        {occurrence?.address && (
          <Tooltip title="Show map">
            <IconButton size="small" onClick={onShowMapClick}>
              <MapIcon sx={{ fontSize: 'inherit' }} />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
    </Card>
  );
}
